import {
  HttpErrorResponse,
  HttpEvent,
  HttpInterceptorFn,
  HttpRequest,
  HttpStatusCode,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, catchError, switchMap, throwError } from 'rxjs';
import { CrmAuthCookieService } from '../pages/auth/services/crm-auth-cookie.service';
import { CrmAuthSessionService } from '../pages/auth/services/crm-auth-session.service';
import { CrmTokenRefreshCoordinator } from '../pages/auth/services/crm-token-refresh.coordinator';
import { environment } from '../../../../../libs/environment/environment';

export const SESSION_EXPIRED_MESSAGE =
  'Your session has expired. Please login again.';

function isApiRequest(url: string, apiBaseUrl: string): boolean {
  return url.startsWith(apiBaseUrl);
}

const AUTH_EXCLUDED_ENDPOINTS = [
  '/v1/business/auth/login',
  '/v1/business/auth/verify-2fa',
  '/v1/business/auth/resend-2fa',
  '/v1/business/auth/refresh',
  '/v1/business/auth/logout',
  '/v1/business/auth/forgot-password',
  '/v1/business/auth/reset-password',
  '/v1/business/auth/set-password',
];

function isAuthExcludedRequest(url: string): boolean {
  const normalizedUrl = url.toLowerCase().split('?')[0];
  return AUTH_EXCLUDED_ENDPOINTS.some((endpoint) =>
    normalizedUrl.endsWith(endpoint),
  );
}

function isUnauthorized(error: unknown): error is HttpErrorResponse {
  return (
    error instanceof HttpErrorResponse &&
    error.status === HttpStatusCode.Unauthorized
  );
}

/**
 * Single owner of 401 handling for the CRM app: refresh the token once if we
 * can, and otherwise end the session with a toast. Any 401 that survives a
 * refresh attempt logs the user out — no API 401 is allowed to surface to a
 * component as a silent failure.
 *
 * This prevents the "tab hangs on loading" issue that occurs when:
 *  - User leaves a tab idle for some time
 *  - Access token expires
 *  - Browser throttles setInterval timers in background tabs
 *  - API requests silently fail with 401, leaving components stuck in loading state
 */
export const crmAuthErrorInterceptor: HttpInterceptorFn = (req, next) => {
  const apiBaseUrl = environment.apiBaseUrl.replace(/\/$/, '');

  // Only intercept API requests, and never auth-specific requests: a 401 from
  // login or verify-2fa means "wrong credentials", not "expired session".
  if (!isApiRequest(req.url, apiBaseUrl) || isAuthExcludedRequest(req.url)) {
    return next(req);
  }

  // Every service must be injected here, in the interceptor's synchronous
  // injection context. inject() inside the catchError callback below runs after
  // the response arrives, when that context is gone, and throws NG0203 — which
  // silently swallows the logout instead of performing it.
  const sessionService = inject(CrmAuthSessionService);
  const cookieService = inject(CrmAuthCookieService);
  const refreshCoordinator = inject(CrmTokenRefreshCoordinator);

  // Capture the session generation at the time the request is made.
  // If the user logs out and back in before this 401 arrives, the generation
  // will have changed and we skip logout to avoid disrupting the new session.
  const requestGeneration = sessionService.getSessionGeneration();

  // Only logout if this 401 belongs to the current session. A stale 401 from a
  // previous session (after logout + re-login) must not clobber the newly
  // established one. CrmAuthSessionService.logout() de-dupes concurrent calls,
  // so parallel 401s produce a single logout and a single toast.
  const endSession = (): void => {
    if (requestGeneration === sessionService.getSessionGeneration()) {
      sessionService.logout(SESSION_EXPIRED_MESSAGE);
    }
  };

  // A 401 on the retried request means the fresh token is not accepted either,
  // so there is nothing left to recover — end the session.
  const retryWithToken = (token: string): Observable<HttpEvent<unknown>> =>
    next(addAuthHeader(req, token)).pipe(
      catchError((retryError: unknown) => {
        if (isUnauthorized(retryError)) {
          endSession();
        }
        return throwError(() => retryError);
      }),
    );

  return next(req).pipe(
    catchError((error: unknown): Observable<HttpEvent<unknown>> => {
      if (!isUnauthorized(error)) {
        return throwError(() => error);
      }

      const refreshToken = cookieService.getRefreshToken();
      const refreshTokenExpiresAt = cookieService.getRefreshTokenExpiresAt();
      const canRefresh =
        !!refreshToken &&
        (refreshTokenExpiresAt === null || refreshTokenExpiresAt > Date.now());

      // No refresh token, or it has already expired — skip refresh and logout
      if (!canRefresh) {
        endSession();
        return throwError(() => error);
      }

      // The coordinator owns the refresh: concurrent 401s in this tab share one
      // in-flight call, and tabs are serialised against each other, so a rotated
      // refresh token is never spent twice. It emits a usable access token or
      // errors when the session cannot be recovered.
      //
      // catchError sits ahead of switchMap deliberately, so it only ever sees a
      // refresh failure. A 401 on the *retried* request is retryWithToken's own
      // business and must not be funnelled through here twice.
      return refreshCoordinator.refresh().pipe(
        catchError((refreshError: unknown) => {
          endSession();
          // An HTTP failure from the refresh call propagates as itself, as
          // before. A logical rejection (no token / expired / not successful)
          // surfaces the original 401, which is what callers used to receive.
          return throwError(() =>
            refreshError instanceof HttpErrorResponse ? refreshError : error,
          );
        }),
        switchMap((newAccessToken) => retryWithToken(newAccessToken)),
      );
    }),
  );
};

function addAuthHeader(
  req: HttpRequest<unknown>,
  token: string,
): HttpRequest<unknown> {
  return req.clone({
    setHeaders: { Authorization: `Bearer ${token}` },
  });
}
